import React from "react";
class Signup extends React.Component {
  constructor(props){
    super(props);
    this.state = {
        stage: 'name_email',
        account: {
            name: "",
            email: "",
            address: "",
            city: "",
            zip: "",
            state: "",
            country: "",
            id_geocode: "",
            user_name: "",
            user_password: ""
        },
        user_password_confirmation: "",
        errors: {},
        form_error: "",
        geocode_loading: false,
        geocode_status: "",
        geocode_suggestion: null,
        submitting: false,
        submitted_account: null
    };

    this.set_address_from_geocode = this.set_address_from_geocode.bind(this);
    this.select_geocode_suggestion = this.select_geocode_suggestion.bind(this);
    this.update_account_field = this.update_account_field.bind(this);
    this.update_password_confirmation = this.update_password_confirmation.bind(this);
    this.next_stage = this.next_stage.bind(this);
    this.previous_stage = this.previous_stage.bind(this);
    this.submit_account = this.submit_account.bind(this);

    this.stages = ['name_email', 'address', 'user_name', 'password', 'review', 'complete'];
    this.geocode_timer = null;
    this.geocode_request_id = 0;

  }

  componentWillUnmount(){
    if (this.geocode_timer) {
      clearTimeout(this.geocode_timer);
    }
  }

  update_account_field(field, value){
    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        [field]: value,
        ...(field === 'address' ? {id_geocode: ""} : {})
      },
      errors: {
        ...prevState.errors,
        [field]: null
      },
      form_error: ""
    }), () => {
      if (field === 'address') {
        this.queue_geocode(value);
      }
    });
  }

  update_password_confirmation(value){
    this.setState((prevState) => ({
      user_password_confirmation: value,
      errors: {
        ...prevState.errors,
        user_password_confirmation: null
      },
      form_error: ""
    }));
  }

  queue_geocode(query){
    if (this.geocode_timer) {
      clearTimeout(this.geocode_timer);
    }

    if (!query || query.trim().length < 4) {
      this.setState({
        geocode_loading: false,
        geocode_status: "",
        geocode_suggestion: null
      });
      return;
    }

    this.geocode_timer = setTimeout(() => this.fetch_geocode(query.trim()), 450);
  }

  fetch_geocode(query){
    const requestId = ++this.geocode_request_id;

    this.setState({
      geocode_loading: true,
      geocode_status: "Looking up address",
      geocode_suggestion: null
    });

    fetch(`/geocode?q=${encodeURIComponent(query)}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': this.props.auth_token
      }
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Address lookup failed');
      }
      return response.json();
    }).then((json) => {
      if (requestId !== this.geocode_request_id) {
        return;
      }

      const result = json.result || json;
      if (result) {
        this.setState({
          geocode_loading: false,
          geocode_status: "",
          geocode_suggestion: result
        });
      } else {
        this.setState({
          geocode_loading: false,
          geocode_status: "No address match found",
          geocode_suggestion: null
        });
      }
    }).catch(() => {
      if (requestId !== this.geocode_request_id) {
        return;
      }

      this.setState({
        geocode_loading: false,
        geocode_status: "Address lookup unavailable",
        geocode_suggestion: null
      });
    });
  }

  set_address_from_geocode(json){
    const component = (type) => {
      const match = (json.address_components || []).find((c) => c.types.includes(type));
      return match ? match.short_name : "";
    };

    this.setState((prevState) => ({
      account: {
        ...prevState.account,
        address: json.formatted_address ? json.formatted_address.split(',')[0] : prevState.account.address,
        city: component('locality') || component('postal_town') || prevState.account.city,
        zip: component('postal_code') || prevState.account.zip,
        state: component('administrative_area_level_1') || prevState.account.state,
        country: component('country') || prevState.account.country,
        id_geocode: json.place_id || prevState.account.id_geocode
      }
    }));
  }

  select_geocode_suggestion(){
    if (!this.state.geocode_suggestion) {
      return;
    }

    this.set_address_from_geocode(this.state.geocode_suggestion);
    this.setState({
      geocode_suggestion: null,
      geocode_status: "Address selected"
    });
  }

  current_stage_index(){
    return this.stages.indexOf(this.state.stage);
  }

  stage_label(stage){
    return {
      name_email: 'Account',
      address: 'Address',
      user_name: 'User',
      password: 'Password',
      review: 'Review',
      complete: 'Complete'
    }[stage];
  }

  validate_stage(stage=this.state.stage){
    const errors = {};
    const account = this.state.account;

    if (stage === 'name_email') {
      if (!account.name.trim()) {
        errors.name = "Account name is required";
      }
      if (!account.email.trim()) {
        errors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(account.email)) {
        errors.email = "Enter a valid email";
      }
    }

    if (stage === 'address') {
      ['address', 'city', 'zip', 'state', 'country'].forEach((field) => {
        if (!account[field].trim()) {
          errors[field] = "Required";
        }
      });
    }

    if (stage === 'user_name' && !account.user_name.trim()) {
      errors.user_name = "Your name is required";
    }

    if (stage === 'password') {
      if (!account.user_password) {
        errors.user_password = "Password is required";
      }
      if (!this.state.user_password_confirmation) {
        errors.user_password_confirmation = "Confirm your password";
      } else if (account.user_password !== this.state.user_password_confirmation) {
        errors.user_password_confirmation = "Passwords do not match";
      }
    }

    this.setState({errors: errors});
    return Object.keys(errors).length === 0;
  }

  validate_all_stages(){
    for (let i = 0; i < this.stages.length; i += 1) {
      const stage = this.stages[i];
      if (stage === 'review' || stage === 'complete') {
        continue;
      }

      if (!this.validate_stage(stage)) {
        this.setState({
          stage: stage
        });
        return false;
      }
    }

    return true;
  }

  next_stage(){
    if (!this.validate_stage()) {
      return;
    }

    const currentIndex = this.current_stage_index();
    if (currentIndex < this.stages.length - 1) {
      this.setState({
        stage: this.stages[currentIndex + 1],
        form_error: ""
      });
    }
  }

  previous_stage(){
    const currentIndex = this.current_stage_index();
    if (currentIndex > 0) {
      this.setState({
        stage: this.stages[currentIndex - 1],
        form_error: ""
      });
    }
  }

  submit_account(event){
    if (event) {
      event.preventDefault();
    }

    if (this.state.stage !== 'review') {
      this.next_stage();
      return;
    }

    if (!this.validate_all_stages()) {
      this.setState({
        form_error: "Please check the highlighted details"
      });
      return;
    }

    this.setState({
      submitting: true,
      form_error: ""
    });

    fetch('/accounts', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': this.props.auth_token
      },
      body: JSON.stringify({
        account: this.state.account
      })
    }).then((response) => {
      return response.json().then((json) => ({
        ok: response.ok,
        json: json
      }));
    }).then(({ok, json}) => {
      if (!ok) {
        this.setState({
          submitting: false,
          form_error: (json.errors || [json.error] || ['Unable to create account']).filter(Boolean).join(', ')
        });
        return;
      }

      this.setState({
        submitting: false,
        submitted_account: json.account || this.state.account,
        stage: 'complete'
      });
    }).catch(() => {
      this.setState({
        submitting: false,
        form_error: "Unable to create account right now"
      });
    });
  }

  field_error(field){
    return this.state.errors[field] ? (
      <div className="psintegril-signup-field-error">{this.state.errors[field]}</div>
    ) : null;
  }

  render_field(field, label, type='text', options={}){
    return (
      <label className="psintegril-signup-field">
        <span>{label}</span>
        <input
          className="psintegril-input"
          type={type}
          value={options.value !== undefined ? options.value : this.state.account[field]}
          autoComplete={options.autoComplete}
          onChange={(event) => {
            if (options.onChange) {
              options.onChange(event.target.value);
            } else {
              this.update_account_field(field, event.target.value);
            }
          }}
        />
        {this.field_error(field)}
      </label>
    );
  }

  render_geocode_suggestion(){
    if (!this.state.geocode_suggestion) {
      return null;
    }

    return (
      <button
        className="psintegril-signup-address-suggestion"
        type="button"
        onClick={this.select_geocode_suggestion}
      >
        <span>Suggested address</span>
        <strong>{this.state.geocode_suggestion.formatted_address}</strong>
      </button>
    );
  }

  render_progress(){
    const currentIndex = this.current_stage_index();

    return (
      <div className="psintegril-signup-progress" aria-label="Signup progress">
        {this.stages.filter((stage) => stage !== 'complete').map((stage, index) => (
          <div
            className={`psintegril-signup-progress-step ${index <= currentIndex ? 'psintegril-signup-progress-step-active' : ''}`}
            key={stage}
          >
            <span>{index + 1}</span>
            {this.stage_label(stage)}
          </div>
        ))}
      </div>
    );
  }

  render_stage(){
    if (this.state.stage === 'name_email') {
      return (
        <div className="psintegril-signup-panel">
          <h2 className="psintegril-h2">Create your account</h2>
          <p className="psintegril-text">Start with the account name and primary email.</p>
          {this.render_field('name', 'Account name', 'text', {autoComplete: 'organization'})}
          {this.render_field('email', 'Email', 'email', {autoComplete: 'email'})}
        </div>
      );
    }

    if (this.state.stage === 'address') {
      return (
        <div className="psintegril-signup-panel">
          <h2 className="psintegril-h2">Account address</h2>
          <p className="psintegril-text">Begin typing the street address to look it up automatically.</p>
          {this.render_field('address', 'Address', 'text', {autoComplete: 'street-address'})}
          {this.render_geocode_suggestion()}
          {this.state.geocode_status ? (
            <div className="psintegril-signup-geocode-status">
              {this.state.geocode_loading ? 'Searching...' : this.state.geocode_status}
            </div>
          ) : null}
          <div className="psintegril-signup-grid">
            {this.render_field('city', 'City', 'text', {autoComplete: 'address-level2'})}
            {this.render_field('zip', 'ZIP / postcode', 'text', {autoComplete: 'postal-code'})}
            {this.render_field('state', 'State', 'text', {autoComplete: 'address-level1'})}
            {this.render_field('country', 'Country', 'text', {autoComplete: 'country-name'})}
          </div>
        </div>
      );
    }

    if (this.state.stage === 'user_name') {
      return (
        <div className="psintegril-signup-panel">
          <h2 className="psintegril-h2">Your details</h2>
          <p className="psintegril-text">This user will be created as the first user for the account.</p>
          {this.render_field('user_name', 'Your name', 'text', {autoComplete: 'name'})}
        </div>
      );
    }

    if (this.state.stage === 'password') {
      return (
        <div className="psintegril-signup-panel">
          <h2 className="psintegril-h2">Set a password</h2>
          <p className="psintegril-text">Password strength checks can be added here later.</p>
          {this.render_field('user_password', 'Password', 'password', {autoComplete: 'new-password'})}
          {this.render_field('user_password_confirmation', 'Confirm password', 'password', {
            value: this.state.user_password_confirmation,
            autoComplete: 'new-password',
            onChange: this.update_password_confirmation
          })}
        </div>
      );
    }

    if (this.state.stage === 'review') {
      const reviewRows = [
        ['Account name', this.state.account.name],
        ['Email', this.state.account.email],
        ['Address', this.state.account.address],
        ['City', this.state.account.city],
        ['ZIP / postcode', this.state.account.zip],
        ['State', this.state.account.state],
        ['Country', this.state.account.country],
        ['User name', this.state.account.user_name],
        ['Password', 'Set']
      ];

      return (
        <div className="psintegril-signup-panel">
          <h2 className="psintegril-h2">Review</h2>
          <p className="psintegril-text">Check these details before creating the account.</p>
          <div className="psintegril-signup-review-table-wrap">
            <table className="psintegril-table psintegril-signup-review-table">
              <tbody>
                {reviewRows.map(([label, value]) => (
                  <tr key={label}>
                    <th scope="row">{label}</th>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div className="psintegril-signup-panel">
        <h2 className="psintegril-h2">Check your email</h2>
        <p className="psintegril-text">We have submitted your account details. Please check {this.state.account.email} for the next step.</p>
      </div>
    );
  }

  render () {
    return (
        <div className="psintegril-signup">
          <div className="psintegril-card psintegril-signup-card">
            {this.state.stage !== 'complete' ? this.render_progress() : null}
            {this.render_stage()}
            {this.state.form_error ? (
              <div className="psintegril-signup-form-error">{this.state.form_error}</div>
            ) : null}
            {this.state.stage !== 'complete' ? (
              <div className="psintegril-signup-actions">
                {this.current_stage_index() > 0 && this.state.stage !== 'complete' ? (
                  <button className="psintegril-btn psintegril-btn-secondary" type="button" onClick={this.previous_stage}>
                    Back
                  </button>
                ) : null}
                {this.state.stage === 'review' ? (
                  <button className="psintegril-btn psintegril-btn-primary" type="button" onClick={this.submit_account} disabled={this.state.submitting}>
                    {this.state.submitting ? 'Creating...' : 'Create account'}
                  </button>
                ) : (
                  <button className="psintegril-btn psintegril-btn-primary" type="button" onClick={this.next_stage}>
                    Continue
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
    );
  }
}

export default Signup;
